import { create } from "zustand";
import type { ChatMessage } from "../data/types";

export type TypingPresence = {
  userId: number;
  name: string;
  expiresAt: number;
};

type MessagesUiState = {
  activeConversationId: string | null;
  /** Conversation currently visible in ChatPanel (null if not on Messages thread UI). */
  viewingConversationId: string | null;
  search: string;
  draft: string;
  contactInfoOpen: boolean;
  threadSearchOpen: boolean;
  threadSearch: string;
  composerMode: "idle" | "dm" | "group";
  typingByConversation: Record<string, TypingPresence | undefined>;
  replyTo: ChatMessage | null;
  forwardMessage: ChatMessage | null;
  setActiveConversationId: (id: string | null) => void;
  setViewingConversationId: (id: string | null) => void;
  setSearch: (value: string) => void;
  setDraft: (value: string) => void;
  setContactInfoOpen: (open: boolean) => void;
  toggleContactInfo: () => void;
  setThreadSearchOpen: (open: boolean) => void;
  setThreadSearch: (value: string) => void;
  setComposerMode: (mode: "idle" | "dm" | "group") => void;
  setPeerTyping: (
    conversationId: string,
    presence: TypingPresence | null,
  ) => void;
  setReplyTo: (message: ChatMessage | null) => void;
  setForwardMessage: (message: ChatMessage | null) => void;
};

export const useMessagesStore = create<MessagesUiState>((set) => ({
  activeConversationId: null,
  viewingConversationId: null,
  search: "",
  draft: "",
  contactInfoOpen: false,
  threadSearchOpen: false,
  threadSearch: "",
  composerMode: "idle",
  typingByConversation: {},
  replyTo: null,
  forwardMessage: null,
  setActiveConversationId: (activeConversationId) =>
    set({
      activeConversationId,
      contactInfoOpen: false,
      threadSearchOpen: false,
      threadSearch: "",
      replyTo: null,
      forwardMessage: null,
    }),
  setViewingConversationId: (viewingConversationId) => set({ viewingConversationId }),
  setSearch: (search) => set({ search }),
  setDraft: (draft) => set({ draft }),
  setContactInfoOpen: (contactInfoOpen) => set({ contactInfoOpen }),
  toggleContactInfo: () =>
    set((state) => ({ contactInfoOpen: !state.contactInfoOpen })),
  setThreadSearchOpen: (threadSearchOpen) =>
    set((state) => ({
      threadSearchOpen,
      threadSearch: threadSearchOpen ? state.threadSearch : "",
    })),
  setThreadSearch: (threadSearch) => set({ threadSearch }),
  setComposerMode: (composerMode) => set({ composerMode }),
  setPeerTyping: (conversationId, presence) =>
    set((state) => {
      const next = { ...state.typingByConversation };
      if (!presence) {
        delete next[conversationId];
      } else {
        next[conversationId] = presence;
      }
      return { typingByConversation: next };
    }),
  setReplyTo: (replyTo) => set({ replyTo }),
  setForwardMessage: (forwardMessage) => set({ forwardMessage }),
}));
